'use client'

import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { Popover, PopoverContent } from '@/components/ui/popover'
import { StateAwareLink } from '@/components/state-aware-link'
import { generateHref } from '@/lib/generate-href'
import { cn } from '@/lib/utils'
import type { HeaderNavigation, VolunteerNavigation } from '@/payload-types'

type HeaderNavItem = NonNullable<HeaderNavigation['items_lg']>[number]
type VolunteerNavItem = NonNullable<VolunteerNavigation['items']>[number]

export type RenderableNavItem = {
  id?: string | null
  label?: string | null
  href?: HeaderNavItem['href'] | VolunteerNavItem['href']
  primary?: boolean | null
  child_items?:
    | {
        id?: string | null
        label?: string | null
        href?: HeaderNavItem['href'] | VolunteerNavItem['href']
      }[]
    | null
}

const headerLinkTypography = 'theme-header-link'

// Slight delays smooth out hover transitions across adjacent triggers without feeling sluggish.
const HOVER_OPEN_DELAY_MS = 80
const HOVER_CLOSE_DELAY_MS = 120

function HeaderNavItemRow({ item, itemIndex }: { item: RenderableNavItem; itemIndex: number }) {
  const [open, setOpen] = React.useState(false)
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = React.useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  React.useEffect(() => clearTimers, [clearTimers])

  const scheduleOpen = React.useCallback(() => {
    clearTimers()
    openTimer.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS)
  }, [clearTimers])

  const scheduleClose = React.useCallback(() => {
    clearTimers()
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS)
  }, [clearTimers])

  const triggerClassName = cn(
    'flex h-full items-center px-4 no-underline',
    headerLinkTypography,
    item.primary
      ? 'bg-theme-button-primary-background hover:bg-theme-button-primary-foreground hover:text-theme-button-primary-background text-theme-button-primary-foreground'
      : 'bg-theme-button-secondary-background text-theme-button-secondary-foreground hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground',
  )

  const triggerLink = (
    <StateAwareLink
      className={triggerClassName}
      href={generateHref(item.href ?? '#')}
      link={item.href ?? '#'}
    >
      {item.label}
    </StateAwareLink>
  )

  if (!item.child_items?.length) {
    return (
      <li className="flex h-full items-center border-l [border-left-width:var(--header-divider-border-width)] border-theme-surface-border">
        {triggerLink}
      </li>
    )
  }

  return (
    <li
      className="flex h-full items-center border-l [border-left-width:var(--header-divider-border-width)] border-theme-surface-border"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocusCapture={() => {
        clearTimers()
        setOpen(true)
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          scheduleClose()
        }
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <span className="flex h-full items-center">{triggerLink}</span>
        </PopoverPrimitive.Anchor>
        <PopoverContent
          align="end"
          sideOffset={0}
          collisionPadding={8}
          // Keep focus on the trigger so hovering doesn't yank focus into the dropdown.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onMouseEnter={() => {
            clearTimers()
            setOpen(true)
          }}
          onMouseLeave={scheduleClose}
          className="w-auto min-w-[220px] rounded-none border [border-width:var(--header-border-width)] border-theme-surface-border bg-theme-card-background p-0 text-foreground shadow-none"
        >
          <ul>
            {item.child_items.map((childItem, childIndex) => (
              <li
                key={childItem.id ?? `top-nav-item-${itemIndex}-child-${childIndex}`}
                className="border-t [border-top-width:var(--header-divider-border-width)] border-theme-surface-border first:border-t-0"
              >
                <StateAwareLink
                  className={cn(
                    'block px-4 py-3 no-underline transition-colors bg-theme-button-secondary-background text-theme-button-secondary-foreground hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground',
                    headerLinkTypography,
                  )}
                  href={generateHref(childItem.href ?? '#')}
                  link={childItem.href ?? '#'}
                >
                  {childItem.label}
                </StateAwareLink>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </li>
  )
}

export function HeaderDesktopNav({ items }: { items: RenderableNavItem[] }) {
  if (!items.length) return null

  return (
    <nav className="hidden h-full lg:block">
      <ul className="flex h-full">
        {items.map((item, itemIndex) => (
          <HeaderNavItemRow
            key={item.id ?? `top-nav-item-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
          />
        ))}
      </ul>
    </nav>
  )
}
